/**
 * Wave A · Invoice Management — Admin Panel (mobile-first).
 *
 * Renders as:
 *   • List view — money tiles + search + filter + card grid (mobile) / table (desktop)
 *   • Create/Edit editor — parent search auto-fill, line-item calculator, live totals,
 *     multi-currency, T&C editor, notes.
 *   • Row actions — Preview (opens /invoice/:token in a new tab), WhatsApp share,
 *     mark paid/cancelled, delete (drafts only).
 *
 * All money math is authoritative on the server — we mirror it locally for
 * responsiveness, then re-hydrate from the server response after save.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
    Plus, Search, FileText, Share2, Copy, Trash2, Loader2, X,
    CheckCircle2, Circle, XCircle, Clock, Wallet, ChevronRight,
    ExternalLink, Save, Eye, IndianRupee, ArrowLeft,
} from "lucide-react";
import { api } from "../../lib/api";
import { SOCIAL } from "../../data/siteData";
import { toast } from "sonner";

const CURRENCIES = [
    { code: "INR", sym: "₹", label: "Indian Rupee" },
    { code: "USD", sym: "$", label: "US Dollar" },
    { code: "GBP", sym: "£", label: "British Pound" },
    { code: "EUR", sym: "€", label: "Euro" },
    { code: "AED", sym: "د.إ", label: "UAE Dirham" },
    { code: "CAD", sym: "C$", label: "Canadian Dollar" },
    { code: "AUD", sym: "A$", label: "Australian Dollar" },
    { code: "SGD", sym: "S$", label: "Singapore Dollar" },
];
const sym = (code) => (CURRENCIES.find((c) => c.code === code) || CURRENCIES[0]).sym;
const money = (v, code = "INR") => `${sym(code)} ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_META = {
    draft: { label: "Draft", cls: "bg-slate-100 text-slate-600", Icon: Circle },
    pending: { label: "Pending", cls: "bg-amber-100 text-amber-700", Icon: Clock },
    partially_paid: { label: "Partially paid", cls: "bg-blue-100 text-blue-700", Icon: Wallet },
    paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
    cancelled: { label: "Cancelled", cls: "bg-rose-100 text-rose-700", Icon: XCircle },
};

// V172 — Primary services + billing-type mapping.
// Keeping `service_type` and `billing_type` on the line item lets the parent-
// facing invoice render smartly: session-based lines show "N sessions × ₹X",
// monthly lines show just "Monthly Fee". `custom` is the legacy escape hatch
// so admins can still write a free-form line (and existing invoices keep
// rendering correctly). "Homework Support" is intentionally not an option.
const SERVICE_OPTIONS = [
    { value: "live_online", label: "1:1 Live Online Classes", billing: "per_session", lockBilling: true },
    { value: "home_tuition", label: "Home Tuitions", billing: "monthly", lockBilling: false },
    { value: "custom", label: "Other / Custom line", billing: "", lockBilling: false },
];
const BILLING_OPTIONS_BY_SERVICE = {
    live_online: [{ value: "per_session", label: "Per Session" }],
    home_tuition: [
        { value: "monthly", label: "Monthly" },
        { value: "per_session", label: "Per Session" },
    ],
    custom: [],
};

const EMPTY_INVOICE = {
    parent_name: "", student_name: "", parent_email: "", parent_phone: "",
    country: "India", state: "", city: "", address: "",
    grade: "", board: "", subjects: "", tuition_mode: "online",
    currency: "INR",
    line_items: [{ description: "1:1 Live Online Classes", quantity: 1, rate: 0, discount: 0, kind: "tuition", service_type: "live_online", billing_type: "per_session" }],
    additional_charges: 0, tax_percent: 0, discount_flat: 0, amount_paid: 0,
    payment_method: "", payment_date: "", transaction_id: "",
    invoice_number: "", invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "", description: "", notes: "", invoice_note: "",
    // V173.2 — Leave blank so the server auto-snapshots the category-matched
    // T&C template on save. Admin can also pre-load it with the "Load category
    // template" button in the Terms section.
    terms: "",
    footer_text: "Thank you for choosing Anand Tutorials.",
    status: null,
};

// ────────────────────────── main panel ────────────────────────────────────
export default function InvoicesPanel({ autoOpen }) {
    const [view, setView] = useState("list"); // list | editor
    const [items, setItems] = useState([]);
    const [tiles, setTiles] = useState({ billed: 0, collected: 0, outstanding: 0 });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [currency, setCurrency] = useState("");
    const [editing, setEditing] = useState(null);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (status) params.set("status", status);
            if (currency) params.set("currency", currency);
            const { data } = await api.get(`/admin/invoices?${params.toString()}`);
            setItems(data.items || []);
            setTiles(data.tiles || { billed: 0, collected: 0, outstanding: 0 });
        } catch (e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [q, status, currency]);

    useEffect(() => { fetchList(); }, [fetchList]);

    // V135.1 — When arriving from Universal Search with a specific
    // invoice id, fetch it directly and open the editor. Falls back
    // silently if the invoice can't be loaded.
    useEffect(() => {
        if (!autoOpen?.id) return;
        (async () => {
            try {
                const { data } = await api.get(`/admin/invoices/${autoOpen.id}`);
                setEditing(data);
                setView("editor");
            } catch { /* ignore — user still sees the list */ }
        })();
    }, [autoOpen?.id, autoOpen?.seq]);

    const openCreate = () => { setEditing({ ...EMPTY_INVOICE }); setView("editor"); };
    const openEdit = (row) => { setEditing({ ...row }); setView("editor"); };

    if (view === "editor" && editing) {
        return (
            <InvoiceEditor
                initial={editing}
                onSaved={() => { setView("list"); setEditing(null); fetchList(); }}
                onCancel={() => { setView("list"); setEditing(null); }}
            />
        );
    }

    return (
        <div className="min-w-0" data-testid="invoices-panel">
            {/* Header */}
            <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-heading text-lg sm:text-xl font-bold text-[#0F172A]">Invoices</h2>
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Create, edit and share invoices with parents.</p>
                </div>
                <button
                    onClick={openCreate}
                    data-testid="invoices-create-btn"
                    className="inline-flex items-center gap-2 bg-[#1E3A8A] hover:bg-[#1e40af] text-white text-sm font-semibold px-4 py-2.5 rounded-lg shadow-sm active:scale-[0.98] transition-all"
                >
                    <Plus className="w-4 h-4" /> Create Invoice
                </button>
            </div>

            {/* Money tiles */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 px-4 sm:px-6 py-4">
                <Tile label="Billed" value={money(tiles.billed, currency || "INR")} tone="slate" />
                <Tile label="Collected" value={money(tiles.collected, currency || "INR")} tone="emerald" />
                <Tile label="Outstanding" value={money(tiles.outstanding, currency || "INR")} tone="amber" />
            </div>

            {/* Search + filters */}
            <div className="px-4 sm:px-6 pb-3 space-y-2">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search invoice #, parent, phone, txn ID…"
                        data-testid="invoices-search"
                        className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-[#1E3A8A] focus:outline-none"
                    />
                </div>
                <div className="flex gap-2 flex-wrap">
                    <FilterChip value={status} onChange={setStatus} options={[
                        { v: "", label: "All statuses" },
                        { v: "draft", label: "Draft" },
                        { v: "pending", label: "Pending" },
                        { v: "partially_paid", label: "Partial" },
                        { v: "paid", label: "Paid" },
                        { v: "cancelled", label: "Cancelled" },
                    ]} testid="invoices-filter-status" />
                    <FilterChip value={currency} onChange={setCurrency} options={[
                        { v: "", label: "All currencies" },
                        ...CURRENCIES.map((c) => ({ v: c.code, label: `${c.code} ${c.sym}` })),
                    ]} testid="invoices-filter-currency" />
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading invoices…
                </div>
            ) : items.length === 0 ? (
                <EmptyState onCreate={openCreate} />
            ) : (
                <div className="px-4 sm:px-6 pb-8 space-y-2.5" data-testid="invoices-list">
                    {items.map((row) => (
                        <InvoiceRow key={row.id} row={row} onEdit={() => openEdit(row)} onChanged={fetchList} />
                    ))}
                </div>
            )}
        </div>
    );
}

const Tile = ({ label, value, tone }) => {
    const toneCls = tone === "emerald" ? "bg-emerald-50 text-emerald-800 border-emerald-100"
        : tone === "amber" ? "bg-amber-50 text-amber-800 border-amber-100"
            : "bg-slate-50 text-slate-800 border-slate-200";
    return (
        <div className={`rounded-xl border ${toneCls} p-3`}>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-60">{label}</div>
            <div className="mt-1 font-bold text-sm sm:text-base truncate">{value}</div>
        </div>
    );
};

const FilterChip = ({ value, onChange, options, testid }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="text-xs sm:text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:border-[#1E3A8A] focus:outline-none">
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
);

const EmptyState = ({ onCreate }) => (
    <div className="text-center py-14 px-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <FileText className="w-5 h-5 text-slate-400" />
        </div>
        <h3 className="font-semibold text-[#0F172A] mb-1">No invoices yet</h3>
        <p className="text-sm text-slate-500 mb-4">Create your first invoice for a paying parent.</p>
        <button onClick={onCreate} data-testid="invoices-empty-create-btn"
            className="inline-flex items-center gap-2 bg-[#1E3A8A] hover:bg-[#1e40af] text-white text-sm font-semibold px-4 py-2 rounded-lg">
            <Plus className="w-4 h-4" /> Create Invoice
        </button>
    </div>
);

// ────────────────────────── row card ──────────────────────────────────────
function InvoiceRow({ row, onEdit, onChanged }) {
    const meta = STATUS_META[row.status] || STATUS_META.draft;
    const publicUrl = `${window.location.origin}/invoice/${row.secure_token}`;

    const share = () => {
        const digits = (row.parent_phone || "").replace(/\D/g, "");
        const msg = `Dear Ma'am/Sir,

Thank you for your payment to Anand Tutorials.

Invoice No: ${row.invoice_number}
${row.student_name ? `Student: ${row.student_name}\n` : ""}Total: ${money(row.total, row.currency)}
Amount Paid: ${money(row.amount_paid, row.currency)}
Balance: ${money(row.balance_due, row.currency)}

View invoice: ${publicUrl}

Thank you for choosing Anand Tutorials.
Website: www.anandtutorials.com
Contact: +91 7000530316`;
        const url = `https://wa.me/${digits || SOCIAL.whatsapp}?text=${encodeURIComponent(msg)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const copyLink = async () => {
        try { await navigator.clipboard.writeText(publicUrl); alert("Link copied"); } catch { }
    };

    const markPaid = async () => {
        if (!confirm(`Mark ${row.invoice_number} as PAID (${money(row.total, row.currency)})?`)) return;
        try {
            await api.post(`/admin/invoices/${row.id}/mark`, {
                status: "paid", amount_paid: row.total,
            });
            onChanged();
        } catch (e) { alert(e?.response?.data?.detail || "Could not mark paid"); }
    };

    const del = async () => {
        if (!confirm(`Delete draft ${row.invoice_number}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/invoices/${row.id}`);
            onChanged();
        } catch (e) { alert(e?.response?.data?.detail || "Could not delete"); }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 hover:shadow-sm transition-all"
            data-testid={`invoice-row-${row.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
                <button onClick={onEdit} className="text-left flex-1 min-w-0" data-testid={`invoice-row-open-${row.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs sm:text-sm font-bold text-[#1E3A8A]">{row.invoice_number}</span>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>
                            <meta.Icon className="w-3 h-3" /> {meta.label}
                        </span>
                    </div>
                    <div className="text-sm font-semibold text-[#0F172A] mt-0.5 truncate">
                        {row.parent_name || "—"}{row.student_name ? <> · <span className="text-slate-500 font-normal">{row.student_name}</span></> : null}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                        {(row.grade || "") + (row.board ? ` · ${row.board}` : "") + (row.subjects ? ` · ${row.subjects}` : "")}
                    </div>
                </button>
                <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-[#0F172A]">{money(row.total, row.currency)}</div>
                    <div className="text-[11px] text-slate-500">Paid {money(row.amount_paid, row.currency)}</div>
                </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
                <a href={publicUrl} target="_blank" rel="noopener noreferrer"
                    data-testid={`invoice-row-preview-${row.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A8A] bg-slate-100 hover:bg-slate-200 px-2.5 py-1.5 rounded-md">
                    <Eye className="w-3 h-3" /> View
                </a>
                <button onClick={share} data-testid={`invoice-row-whatsapp-${row.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1.5 rounded-md">
                    <Share2 className="w-3 h-3" /> WhatsApp
                </button>
                <button onClick={copyLink} data-testid={`invoice-row-copy-${row.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 px-2.5 py-1.5 rounded-md">
                    <Copy className="w-3 h-3" /> Link
                </button>
                {row.status !== "paid" && row.status !== "cancelled" && (
                    <button onClick={markPaid} data-testid={`invoice-row-mark-paid-${row.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 px-2.5 py-1.5 rounded-md">
                        <CheckCircle2 className="w-3 h-3" /> Mark paid
                    </button>
                )}
                <button onClick={onEdit} data-testid={`invoice-row-edit-${row.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 px-2.5 py-1.5 rounded-md">
                    Edit <ChevronRight className="w-3 h-3" />
                </button>
                {row.status === "draft" && (
                    <button onClick={del} data-testid={`invoice-row-delete-${row.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 px-2.5 py-1.5 rounded-md ml-auto">
                        <Trash2 className="w-3 h-3" />
                    </button>
                )}
            </div>
        </div>
    );
}

// ────────────────────────── editor ────────────────────────────────────────
function InvoiceEditor({ initial, onSaved, onCancel }) {
    const isEdit = !!initial.id;
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [parentQuery, setParentQuery] = useState("");
    const [parentResults, setParentResults] = useState([]);
    const [showResults, setShowResults] = useState(false);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    // V194 — show the next auto number in the field once per draft (stable across edits/re-renders).
    const suggestedNumber = useRef("");
    const fetchNumber = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/invoices/next-number");
            suggestedNumber.current = data.invoice_number;
            set({ invoice_number: data.invoice_number });
        } catch { /* blank → server assigns on save */ }
    }, []);
    useEffect(() => {
        if (!isEdit && !initial.invoice_number) fetchNumber();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // V194 — new invoices show the category's policy; switching category swaps it unless the admin typed their own.
    const autoTerms = useRef(null);
    useEffect(() => {
        if (isEdit) return;
        let alive = true;
        api.get(`/admin/invoice-terms/preview?tuition_mode=${encodeURIComponent(form.tuition_mode || "online")}`)
            .then(({ data }) => {
                if (!alive) return;
                const prev = autoTerms.current;
                const next = data.terms || "";
                autoTerms.current = next;
                setForm((f) => ((!f.terms || f.terms === prev) ? { ...f, terms: next } : f));
            }).catch(() => {});
        return () => { alive = false; };
    }, [form.tuition_mode, isEdit]);

    // Local mirror of the server calc (server is authoritative on save)
    const totals = useMemo(() => {
        let subtotal = 0, discSum = 0;
        for (const li of (form.line_items || [])) {
            const q = Number(li.quantity || 0), r = Number(li.rate || 0), d = Number(li.discount || 0);
            subtotal += q * r; discSum += d;
        }
        const discount_total = discSum + Number(form.discount_flat || 0);
        const taxable = Math.max(0, subtotal - discount_total);
        const tax_total = taxable * (Number(form.tax_percent || 0) / 100);
        const total = taxable + tax_total + Number(form.additional_charges || 0);
        const amount_paid = Math.min(Number(form.amount_paid || 0), total);
        return {
            subtotal, discount_total, tax_total, total, amount_paid,
            balance_due: total - amount_paid,
        };
    }, [form]);

    // Debounced parent search
    useEffect(() => {
        if (parentQuery.trim().length < 2) { setParentResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/admin/invoices/parents/search?q=${encodeURIComponent(parentQuery.trim())}`);
                setParentResults(data.items || []); setShowResults(true);
            } catch { setParentResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [parentQuery]);

    const pickParent = (p) => {
        setForm((f) => ({
            ...f,
            parent_name: p.parent_name || f.parent_name,
            student_name: p.student_name || f.student_name,
            parent_email: p.parent_email || f.parent_email,
            parent_phone: p.parent_phone || f.parent_phone,
            country: p.country || f.country,
            state: p.state || f.state,
            city: p.city || f.city,
            address: p.address || f.address,
            grade: p.grade || f.grade,
            board: p.board || f.board,
            subjects: p.subjects || f.subjects,
            tuition_mode: p.tuition_mode || f.tuition_mode,
            registration_id: p.registration_id || f.registration_id,
        }));
        setParentQuery(""); setShowResults(false);
        try {
            const src = p.source_label || "record";
            toast.success(`Auto-filled from ${src}`);
        } catch { /* toast optional */ }
    };

    const updateLine = (i, patch) => setForm((f) => ({
        ...f, line_items: f.line_items.map((li, j) => j === i ? { ...li, ...patch } : li),
    }));
    // Smart service-change handler: picks the right default billing type +
    // description, and resets quantity sensibly when switching modes.
    const changeLineService = (i, serviceValue) => {
        const svc = SERVICE_OPTIONS.find((s) => s.value === serviceValue);
        if (!svc) return;
        const defaultBilling = svc.billing;
        const curr = form.line_items[i] || {};
        const nextDesc = (svc.value === "custom") ? (curr.description || "") : svc.label;
        // Monthly stores quantity=1 (hidden from UI) because the money math is
        // quantity × rate; the UI/PDF just suppress the "1 ×" display.
        const nextQty = defaultBilling === "monthly" ? 1 : (curr.quantity && curr.quantity > 0 ? curr.quantity : 1);
        updateLine(i, {
            service_type: svc.value,
            billing_type: defaultBilling,
            description: nextDesc,
            quantity: nextQty,
        });
    };
    const changeLineBilling = (i, billingValue) => {
        const curr = form.line_items[i] || {};
        const nextQty = billingValue === "monthly" ? 1 : (curr.quantity && curr.quantity > 0 ? curr.quantity : 1);
        updateLine(i, { billing_type: billingValue, quantity: nextQty });
    };
    const addLine = () => setForm((f) => ({
        ...f, line_items: [...f.line_items, { description: "Home Tuitions", quantity: 1, rate: 0, discount: 0, kind: "tuition", service_type: "home_tuition", billing_type: "monthly" }],
    }));
    const removeLine = (i) => setForm((f) => ({
        ...f, line_items: f.line_items.filter((_, j) => j !== i),
    }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                line_items: (form.line_items || []).map((li) => ({
                    description: li.description || "",
                    quantity: Number(li.quantity || 0),
                    rate: Number(li.rate || 0),
                    discount: Number(li.discount || 0),
                    kind: li.kind || "tuition",
                    service_type: li.service_type || "",
                    billing_type: li.billing_type || "",
                })),
                additional_charges: Number(form.additional_charges || 0),
                tax_percent: Number(form.tax_percent || 0),
                discount_flat: Number(form.discount_flat || 0),
                amount_paid: Number(form.amount_paid || 0),
                auto_number: !isEdit && !!suggestedNumber.current && (form.invoice_number || "").trim() === suggestedNumber.current,
            };
            if (isEdit) {
                await api.put(`/admin/invoices/${form.id}`, payload);
            } else {
                const { data } = await api.post(`/admin/invoices`, payload);
                if (payload.auto_number && data.invoice_number !== payload.invoice_number) {
                    toast.info(`${payload.invoice_number} was just taken — saved as ${data.invoice_number}`);
                }
            }
            onSaved();
        } catch (e) {
            alert(e?.response?.data?.detail || "Could not save invoice");
        } finally { setSaving(false); }
    };

    return (
        <div className="min-w-0" data-testid="invoice-editor">
            {/* Sticky editor header */}
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
                <button onClick={onCancel} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#1E3A8A]" data-testid="invoice-editor-back">
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <h3 className="font-heading font-bold text-[#0F172A] text-base sm:text-lg flex-1 min-w-0 truncate">
                    {isEdit ? `Edit ${form.invoice_number || "invoice"}` : "New Invoice"}
                </h3>
                <button onClick={save} disabled={saving} data-testid="invoice-editor-save"
                    className="inline-flex items-center gap-1.5 bg-[#1E3A8A] hover:bg-[#1e40af] disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? "Saving…" : (isEdit ? "Save" : "Create")}
                </button>
            </div>

            <div className="px-4 sm:px-6 py-4 space-y-6">
                {/* Parent search */}
                {!isEdit && (
                    <Section title="Bill to" hint="Type a name, phone or email — matching registrations, inquiries and app leads populate every field below.">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input value={parentQuery} onChange={(e) => setParentQuery(e.target.value)}
                                onFocus={() => setShowResults(true)}
                                placeholder="Search parent / student by name, phone or email…"
                                data-testid="invoice-editor-parent-search"
                                className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-[#1E3A8A] focus:outline-none" />
                            {showResults && parentResults.length > 0 && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-auto" data-testid="invoice-parent-results">
                                    {parentResults.map((p) => {
                                        const dateStr = p.created_at ? (() => { try { return new Date(p.created_at).toLocaleDateString(); } catch { return ""; } })() : "";
                                        const srcCls = p.source === "inquiry"
                                            ? "bg-amber-100 text-amber-700"
                                            : p.source === "app_lead"
                                                ? "bg-slate-100 text-slate-700"
                                                : "bg-blue-100 text-blue-700";
                                        return (
                                        <button key={`${p.source}-${p.registration_id}`} type="button" onClick={() => pickParent(p)}
                                            data-testid={`invoice-parent-pick-${p.registration_id}`}
                                            className="w-full text-left px-3 py-2.5 hover:bg-slate-50 active:bg-slate-100 border-b border-slate-100 last:border-0">
                                            <div className="flex items-start gap-2 flex-wrap">
                                                <span className="text-sm font-semibold text-[#0F172A] flex-1 min-w-0 truncate">
                                                    {p.parent_name || "—"}
                                                    {p.student_name ? <span className="text-slate-500 font-normal"> · {p.student_name}</span> : null}
                                                </span>
                                                <span className={`shrink-0 text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded-full ${srcCls}`} data-testid={`invoice-parent-pick-src-${p.source}`}>
                                                    {p.source_label || p.source}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate mt-0.5">
                                                {p.parent_phone || "no phone"}
                                                {p.parent_email ? ` · ${p.parent_email}` : ""}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate mt-0.5">
                                                {[
                                                    p.grade && (/^class\b/i.test(String(p.grade).trim()) ? p.grade : `Class ${p.grade}`),
                                                    p.board,
                                                    p.city && [p.city, p.state].filter(Boolean).join(", "),
                                                ].filter(Boolean).join(" · ") || "no location on file"}
                                                {dateStr ? <span className="text-slate-400"> · {dateStr}</span> : null}
                                            </div>
                                        </button>
                                        );
                                    })}
                                </div>
                            )}
                            {showResults && parentQuery.trim().length >= 2 && parentResults.length === 0 && (
                                <div className="mt-2 text-[11px] text-slate-500 italic" data-testid="invoice-parent-no-results">
                                    No prior contact found — fill in the details manually below.
                                </div>
                            )}
                        </div>
                    </Section>
                )}

                {/* Bill-to fields */}
                <Section title="Parent & student details">
                    <Grid>
                        <Field label="Parent name *" value={form.parent_name} onChange={(v) => set({ parent_name: v })} testid="inv-parent-name" />
                        <Field label="Student name" value={form.student_name} onChange={(v) => set({ student_name: v })} testid="inv-student-name" />
                        <Field label="Parent email" value={form.parent_email} onChange={(v) => set({ parent_email: v })} type="email" testid="inv-parent-email" />
                        <Field label="Parent phone" value={form.parent_phone} onChange={(v) => set({ parent_phone: v })} testid="inv-parent-phone" hint="Auto-fills the WhatsApp share." />
                        <Field label="Country" value={form.country} onChange={(v) => set({ country: v })} testid="inv-country" />
                        <Field label="State" value={form.state} onChange={(v) => set({ state: v })} testid="inv-state" />
                        <Field label="City" value={form.city} onChange={(v) => set({ city: v })} testid="inv-city" />
                        <Field label="Grade / class" value={form.grade} onChange={(v) => set({ grade: v })} testid="inv-grade" />
                        <Field label="Board / curriculum" value={form.board} onChange={(v) => set({ board: v })} testid="inv-board" />
                        <Field label="Subjects" value={form.subjects} onChange={(v) => set({ subjects: v })} testid="inv-subjects" full />
                        <Field label="Address" value={form.address} onChange={(v) => set({ address: v })} testid="inv-address" full />
                    </Grid>
                </Section>

                {/* Invoice meta */}
                <Section title="Invoice details">
                    <Grid>
                        <Field label="Invoice number" value={form.invoice_number || ""} onChange={(v) => set({ invoice_number: v })} testid="inv-number" mono
                            placeholder="Leave blank for auto"
                            hint={isEdit ? "Changing it updates this same invoice." : "Auto-generated — edit it if you prefer a different number."}
                            action={!isEdit && (
                                <button type="button" onClick={(e) => { e.preventDefault(); fetchNumber(); }} data-testid="inv-number-new"
                                    className="text-[11px] font-semibold text-[#1E3A8A] hover:underline">New number</button>
                            )} />
                        <Field label="Currency" value={form.currency} onChange={(v) => set({ currency: v })} testid="inv-currency" select options={CURRENCIES.map((c) => ({ v: c.code, label: `${c.code} ${c.sym}` }))} />
                        <Field label="Invoice date" value={form.invoice_date || ""} onChange={(v) => set({ invoice_date: v })} type="date" testid="inv-date" />
                        <Field label="Due date" value={form.due_date || ""} onChange={(v) => set({ due_date: v })} type="date" testid="inv-due-date" />
                        {/* V173.2 — Invoice category drives which T&C template
                            is auto-applied on save. Admin can still override by
                            typing custom text in the Terms field below. */}
                        <Field
                            label="Invoice category"
                            value={form.tuition_mode}
                            onChange={(v) => set({ tuition_mode: v })}
                            testid="inv-tuition-mode"
                            select
                            options={[
                                { v: "online", label: "1:1 Online Classes" },
                                { v: "home", label: "Home Tuitions" },
                                { v: "hybrid", label: "Hybrid" },
                                { v: "makeover", label: "Makeover / Other" },
                            ]}
                            hint="Controls which Terms & Conditions template is applied by default."
                            full
                        />
                    </Grid>
                </Section>

                {/* Line items — V172 service-aware */}
                <Section title="Services" hint="Pick a service, set the rate, and totals update live.">
                    <div className="space-y-3">
                        {(form.line_items || []).map((li, i) => {
                            const service = li.service_type || "custom";
                            const billing = li.billing_type || "";
                            const svc = SERVICE_OPTIONS.find((s) => s.value === service) || SERVICE_OPTIONS[2];
                            const billingOpts = BILLING_OPTIONS_BY_SERVICE[service] || [];
                            const isMonthly = service === "home_tuition" && billing === "monthly";
                            const isSessionBased = service === "live_online" || (service === "home_tuition" && billing === "per_session");
                            const isCustom = service === "custom";
                            const amount = isMonthly
                                ? Math.max(0, (li.rate || 0) - (li.discount || 0))
                                : Math.max(0, (li.quantity || 0) * (li.rate || 0) - (li.discount || 0));
                            return (
                                <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`invoice-line-${i}`} data-service-type={service} data-billing-type={billing}>
                                    {/* Service + billing header */}
                                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2">
                                        <div className="sm:col-span-6">
                                            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">Service</label>
                                            <select
                                                value={service}
                                                onChange={(e) => changeLineService(i, e.target.value)}
                                                data-testid={`inv-line-service-${i}`}
                                                className="w-full mt-1 px-2 py-2 border border-slate-200 rounded-md text-sm bg-white"
                                            >
                                                {SERVICE_OPTIONS.map((s) => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {billingOpts.length > 0 && (
                                            <div className="sm:col-span-6">
                                                <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">Billing Type</label>
                                                {svc.lockBilling || billingOpts.length === 1 ? (
                                                    <div className="w-full mt-1 px-2 py-2 border border-slate-200 rounded-md text-sm bg-white text-slate-600" data-testid={`inv-line-billing-${i}`}>
                                                        {billingOpts[0]?.label || "Per Session"}
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-2 mt-1" role="radiogroup" aria-label="Billing Type">
                                                        {billingOpts.map((b) => (
                                                            <button
                                                                key={b.value}
                                                                type="button"
                                                                role="radio"
                                                                aria-checked={billing === b.value}
                                                                onClick={() => changeLineBilling(i, b.value)}
                                                                data-testid={`inv-line-billing-${i}-${b.value}`}
                                                                className={`flex-1 text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
                                                                    billing === b.value
                                                                        ? "bg-[#1E3A8A] border-[#1E3A8A] text-white"
                                                                        : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                                                                }`}
                                                            >
                                                                {b.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Smart pricing fields */}
                                    {isCustom && (
                                        <div className="grid grid-cols-12 gap-2 items-end">
                                            <div className="col-span-12 sm:col-span-5">
                                                <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">Description</label>
                                                <input value={li.description} onChange={(e) => updateLine(i, { description: e.target.value })}
                                                    data-testid={`inv-line-desc-${i}`}
                                                    placeholder="e.g. Study material kit"
                                                    className="w-full mt-1 px-2 py-1.5 border border-slate-200 rounded-md text-sm bg-white" />
                                            </div>
                                            <NumField label="Qty" i={i} value={li.quantity} onChange={(v) => updateLine(i, { quantity: v })} testid={`inv-line-qty-${i}`} className="col-span-3 sm:col-span-2" />
                                            <NumField label="Rate" i={i} value={li.rate} onChange={(v) => updateLine(i, { rate: v })} testid={`inv-line-rate-${i}`} className="col-span-4 sm:col-span-2" />
                                            <NumField label="Discount" i={i} value={li.discount} onChange={(v) => updateLine(i, { discount: v })} testid={`inv-line-disc-${i}`} className="col-span-4 sm:col-span-2" />
                                            <div className="col-span-4 sm:col-span-1 text-right pr-1">
                                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">Amount</div>
                                                <div className="mt-1 font-semibold text-[#0F172A] text-sm" data-testid={`inv-line-amount-${i}`}>
                                                    {money(amount, form.currency)}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {isSessionBased && (
                                        <div className="grid grid-cols-12 gap-2 items-end">
                                            <NumField label="Number of Sessions" i={i} value={li.quantity} onChange={(v) => updateLine(i, { quantity: v })} testid={`inv-line-qty-${i}`} className="col-span-6 sm:col-span-3" />
                                            <NumField label="Rate / Session" i={i} value={li.rate} onChange={(v) => updateLine(i, { rate: v })} testid={`inv-line-rate-${i}`} className="col-span-6 sm:col-span-3" />
                                            <NumField label="Discount" i={i} value={li.discount} onChange={(v) => updateLine(i, { discount: v })} testid={`inv-line-disc-${i}`} className="col-span-6 sm:col-span-3" />
                                            <div className="col-span-6 sm:col-span-3 text-right pr-1">
                                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">Amount</div>
                                                <div className="mt-1 font-semibold text-[#0F172A] text-sm" data-testid={`inv-line-amount-${i}`}>
                                                    {money(amount, form.currency)}
                                                </div>
                                                <div className="text-[10px] text-slate-500 mt-0.5">
                                                    {Number(li.quantity || 0)} × {money(li.rate || 0, form.currency)}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {isMonthly && (
                                        <div className="grid grid-cols-12 gap-2 items-end">
                                            <NumField label="Monthly Fee" i={i} value={li.rate} onChange={(v) => updateLine(i, { rate: v })} testid={`inv-line-rate-${i}`} className="col-span-6 sm:col-span-4" />
                                            <NumField label="Discount" i={i} value={li.discount} onChange={(v) => updateLine(i, { discount: v })} testid={`inv-line-disc-${i}`} className="col-span-6 sm:col-span-4" />
                                            <div className="col-span-12 sm:col-span-4 text-right pr-1">
                                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">Amount</div>
                                                <div className="mt-1 font-semibold text-[#0F172A] text-sm" data-testid={`inv-line-amount-${i}`}>
                                                    {money(amount, form.currency)}
                                                </div>
                                                <div className="text-[10px] text-slate-500 mt-0.5">Monthly Fee</div>
                                            </div>
                                        </div>
                                    )}

                                    {(form.line_items.length > 1) && (
                                        <div className="flex justify-end mt-2">
                                            <button onClick={() => removeLine(i)} data-testid={`inv-line-remove-${i}`} className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-800">
                                                <Trash2 className="w-3.5 h-3.5" /> Remove service
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <button onClick={addLine} data-testid="invoice-line-add" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A8A] hover:text-[#1e40af]">
                        <Plus className="w-3 h-3" /> Add another service
                    </button>
                </Section>

                {/* Totals + adjustments */}
                <Section title="Totals & payment">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <NumStack label="Discount (flat)" value={form.discount_flat} onChange={(v) => set({ discount_flat: v })} testid="inv-discount-flat" />
                            <NumStack label="Tax %" value={form.tax_percent} onChange={(v) => set({ tax_percent: v })} testid="inv-tax-percent" />
                            <NumStack label="Additional charges" value={form.additional_charges} onChange={(v) => set({ additional_charges: v })} testid="inv-additional-charges" />
                            <NumStack label="Amount paid" value={form.amount_paid} onChange={(v) => set({ amount_paid: v })} testid="inv-amount-paid" />
                            <Field label="Payment method" value={form.payment_method} onChange={(v) => set({ payment_method: v })} testid="inv-payment-method" select options={[
                                { v: "", label: "—" },
                                { v: "razorpay", label: "Razorpay" },
                                { v: "upi", label: "UPI" },
                                { v: "bank_transfer", label: "Bank transfer" },
                                { v: "card", label: "Card" },
                                { v: "cash", label: "Cash" },
                                { v: "wise", label: "Wise" },
                                { v: "paypal", label: "PayPal" },
                                { v: "stripe", label: "Stripe" },
                            ]} />
                            <Field label="Payment date" value={form.payment_date || ""} onChange={(v) => set({ payment_date: v })} type="date" testid="inv-payment-date" />
                            <Field label="Transaction / reference ID" value={form.transaction_id} onChange={(v) => set({ transaction_id: v })} testid="inv-txn-id" mono />
                        </div>
                        <div className="bg-slate-900 text-white rounded-xl p-4 space-y-2 self-start">
                            <TotalRow k="Subtotal" v={money(totals.subtotal, form.currency)} />
                            {totals.discount_total > 0 && (
                                <TotalRow k="Discount" v={`- ${money(totals.discount_total, form.currency)}`} muted />
                            )}
                            {totals.tax_total > 0 && (
                                <TotalRow k={`Tax (${form.tax_percent || 0}%)`} v={money(totals.tax_total, form.currency)} muted />
                            )}
                            {Number(form.additional_charges || 0) > 0 && (
                                <TotalRow k="Additional" v={money(form.additional_charges || 0, form.currency)} muted />
                            )}
                            <div className="border-t border-white/20 pt-2 mt-2">
                                <TotalRow k="TOTAL" v={money(totals.total, form.currency)} big />
                            </div>
                            <TotalRow k="Amount paid" v={money(totals.amount_paid, form.currency)} muted />
                            <div className="border-t border-white/20 pt-2 mt-1">
                                <TotalRow k="BALANCE DUE" v={money(totals.balance_due, form.currency)} big tone={totals.balance_due > 0 ? "amber" : "emerald"} />
                            </div>
                        </div>
                    </div>
                </Section>

                {/* Description + T&C */}
                <Section title="Description, terms & notes">
                    <TextArea label="Description shown on invoice" value={form.description} onChange={(v) => set({ description: v })} testid="inv-description" rows={2} />
                    {/* V172 — Parent-facing per-invoice Note. Hidden on the public
                        invoice when empty — never displays "N/A" or an empty box. */}
                    <TextArea
                        label="Note (optional) — shown to the parent on this invoice only"
                        value={form.invoice_note || ""}
                        onChange={(v) => set({ invoice_note: v })}
                        testid="inv-invoice-note"
                        rows={3}
                    />
                    {/* V173.2 — Terms + "Load template" button so admin can
                        preview/overwrite the terms textarea with the matching
                        category template on demand. The server also auto-
                        snapshots the right template on save if this field is
                        left blank. */}
                    <div>
                        <div className="flex items-end justify-between gap-2 mb-1">
                            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                Terms & conditions (shown on invoice)
                            </label>
                            <button
                                type="button"
                                onClick={async () => {
                                    if (form.terms && !window.confirm("Overwrite the current terms with the category template?")) return;
                                    try {
                                        const r = await api.get(`/admin/invoice-terms/preview?tuition_mode=${encodeURIComponent(form.tuition_mode || "online")}`);
                                        autoTerms.current = r.data.terms || "";
                                        set({ terms: r.data.terms || "" });
                                        toast.success(`Loaded ${r.data.category === "home_tuition" ? "Home Tuitions" : "1:1 Online Classes"} template.`);
                                    } catch (e) {
                                        toast.error("Could not load template.");
                                    }
                                }}
                                data-testid="inv-terms-load-template"
                                className="text-[11px] font-semibold text-[#1E3A8A] hover:text-[#1e40af] inline-flex items-center gap-1"
                            >
                                Load category template
                            </button>
                        </div>
                        <TextArea label="" value={form.terms} onChange={(v) => set({ terms: v })} testid="inv-terms" rows={8} hint="Leave blank to auto-apply the correct template on save, based on the invoice category." />
                    </div>
                    <TextArea label="Footer text" value={form.footer_text} onChange={(v) => set({ footer_text: v })} testid="inv-footer" rows={2} />
                    <TextArea label="Internal notes (admin-only, never shown to parent)" value={form.notes} onChange={(v) => set({ notes: v })} testid="inv-notes" rows={2} />
                </Section>

                {/* Sticky mobile bottom save */}
                <div className="sticky bottom-3 sm:static bg-white py-2 -mx-1">
                    <button onClick={save} disabled={saving} data-testid="invoice-editor-save-bottom"
                        className="w-full inline-flex items-center justify-center gap-1.5 bg-[#1E3A8A] hover:bg-[#1e40af] disabled:opacity-60 text-white text-sm font-semibold py-3 rounded-lg shadow-lg">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? "Saving…" : (isEdit ? "Save changes" : "Create invoice")}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ────────────────────────── mini building blocks ──────────────────────────
const Section = ({ title, hint, children }) => (
    <section>
        <div className="mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{title}</div>
            {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
        </div>
        {children}
    </section>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;

const Field = ({ label, value, onChange, type = "text", testid, mono, full, select, options, hint, placeholder, action }) => (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
        <span className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">{label}</span>
            {action}
        </span>
        {select ? (
            <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className={`w-full mt-1 px-2 py-2 border border-slate-200 rounded-md text-sm bg-white ${mono ? "font-mono" : ""}`}>
                {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
        ) : (
            <input value={value || ""} onChange={(e) => onChange(e.target.value)} type={type} data-testid={testid} placeholder={placeholder}
                className={`w-full mt-1 px-2 py-2 border border-slate-200 rounded-md text-sm bg-white ${mono ? "font-mono" : ""}`} />
        )}
        {hint && <span className="block text-[11px] text-slate-500 mt-0.5">{hint}</span>}
    </label>
);

const NumField = ({ label, value, onChange, testid, className = "" }) => (
    <div className={className}>
        <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">{label}</span>
            <input type="number" inputMode="decimal" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))}
                data-testid={testid} className="w-full mt-1 px-2 py-1.5 border border-slate-200 rounded-md text-sm bg-white" />
        </label>
    </div>
);

const NumStack = ({ label, value, onChange, testid }) => (
    <label className="block">
        <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">{label}</span>
        <input type="number" inputMode="decimal" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))}
            data-testid={testid} className="w-full mt-1 px-2 py-2 border border-slate-200 rounded-md text-sm bg-white" />
    </label>
);

const TextArea = ({ label, value, onChange, testid, rows = 3 }) => (
    <label className="block mb-3">
        <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">{label}</span>
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid}
            className="w-full mt-1 px-2 py-2 border border-slate-200 rounded-md text-sm bg-white leading-relaxed" />
    </label>
);

const TotalRow = ({ k, v, big, muted, tone }) => (
    <div className={`flex items-baseline justify-between ${muted ? "opacity-80" : ""}`}>
        <span className={`text-xs ${big ? "font-bold tracking-wider" : ""}`}>{k}</span>
        <span className={`${big ? "text-lg font-bold" : "text-sm font-semibold"} ${tone === "amber" ? "text-amber-300" : tone === "emerald" ? "text-emerald-300" : ""}`}>{v}</span>
    </div>
);
